import { all, call, put, takeEvery, select } from 'redux-saga/effects';
import { defineKqlQuery, searchSubmissions } from '@kineticdata/react';
import { selectServicesKapp, C } from '@kineticdata/bundle-common';
import { actions, types, searchFields, typeStates } from '../modules/requests';
import { Map } from 'immutable';

const buildSearch = (search, profile) => {
  // Check if filter query is provided
  const isFilterQuery = Array.isArray(search.query) && search.query.length > 0;
  // Define query searcher
  const searcher = defineKqlQuery();
  // Add type and coreState
  searcher.equals('type', 'type').equals('coreState', 'coreState');

  // Create list of filter parts added in assignments so we can exclude them later
  let filterPartExclusions = [];
  // Add assignment parts
  if (search.coreState === C.CORE_STATE_DRAFT) {
    // If searching for drafts
    searcher.equals('createdBy', 'username');
  } else if (search.all) {
    // If searching through all requests (not just mine)
    if (!profile.superUser && !profile.superUserRO) {
      searcher.in(`values[${C.FIELD_ASSIGNED_TEAM}]`, 'agreements');
      filterPartExclusions = [
        ...filterPartExclusions,
        `values[${C.FIELD_ASSIGNED_TEAM}]`,
      ];
    }
  } else {
    // If searching through my requests
    if (
      isFilterQuery &&
      search.query.find(({ field }) => field && field.id === 'for')
    ) {
      // If filtering by Requested For, only add submittedBy me part
      searcher.equals('submittedBy', 'username');
      filterPartExclusions = [...filterPartExclusions, 'submittedBy'];
    } else if (
      // If filtering by Submitted By, only add Requested For me part
      isFilterQuery &&
      search.query.find(({ field }) => field && field.id === 'by')
    ) {
      searcher.equals(`values[${C.FIELD_REQUESTED_FOR}]`, 'username');
      filterPartExclusions = [
        ...filterPartExclusions,
        `values[${C.FIELD_REQUESTED_FOR}]`,
      ];
    } else {
      // If neither is included in filter query, add both as OR statements
      searcher
        .or()
        .equals('submittedBy', 'username')
        .equals(`values[${C.FIELD_REQUESTED_FOR}]`, 'username')
        .end();
      filterPartExclusions = [
        ...filterPartExclusions,
        'submittedBy',
        `values[${C.FIELD_REQUESTED_FOR}]`,
      ];
    }
  }
  // Add filter parts
  let searcherFilterValues = {};
  if (Array.isArray(search.query)) {
    search.query.forEach(({ field, value }) => {
      if (!field) {
        searcher.or();
        for (let i = 0; i < searchFields.length; i++) {
          if (!filterPartExclusions.includes(searchFields[i].value)) {
            searcher.equals(searchFields[i].value, '_all');
          }
        }
        searcher.end();
        searcherFilterValues['_all'] = value;
      } else {
        if (!filterPartExclusions.includes(field.value)) {
          searcher.equals(field.value, field.id);
          searcherFilterValues[field.id] = value;
        }
      }
    });
  }

  return {
    q: searcher.end()({
      type: C.REQUEST_FORM_TYPE,
      coreState: search.coreState,
      username: profile.username,
      agreements: profile.hasAgreements ? profile.agreements : ['_none_'],
      ...searcherFilterValues,
    }),
    include: [
      'details',
      'values',
      'form',
      'form.attributes',
      'form.kapp',
      'form.kapp.attributes',
      'descendants',
    ],
  };
};

export function* fetchRequestsRequestSaga(action) {
  const kapp = yield select(selectServicesKapp);
  const profile = yield select(state => state.app.profile);
  const search = yield select(state => state.requests.search);
  const limit = yield select(state => state.requests.limit);
  const direction = yield select(state => state.requests.direction);
  const pageToken = yield select(state => state.requests.pageToken);
  const {
    submissions,
    error,
    nextPageToken,
    count,
    countPageToken,
  } = yield call(searchSubmissions, {
    kapp: (kapp && kapp.slug) || 'services',
    limit: action.payload && action.payload.homepage ? 5 : limit || 10,
    pageToken,
    orderBy:
      search.coreState === C.CORE_STATE_CLOSED ? 'closedAt' : 'createdAt',
    direction: action.payload && action.payload.homepage ? 'DESC' : direction,
    search: buildSearch(search, profile),
    count: true,
  });

  if (error) {
    yield put(actions.fetchRequestsFailure({ error }));
  } else {
    yield put(
      actions.fetchRequestsSuccess({
        data: submissions,
        nextPageToken,
        count,
        countPageToken,
      }),
    );
    // If fetching the first page, update the count
    if (!pageToken) {
      const type = typeStates.findKey(
        (coreState, type) =>
          coreState === search.coreState &&
          type.includes('Other') === !!search.all,
      );
      yield put(actions.countRequestsComplete({ [type]: count }));

      // If fetching open requests with no filters, update home requests count
      // which is shown in the sidebar
      if (
        search.coreState === C.CORE_STATE_SUBMITTED &&
        !search.all &&
        (!search.query || search.query.length === 0)
      ) {
        const setHomeRequestsCount = yield select(
          state => state.app.actions.setHomeRequestsCount,
        );
        if (typeof setHomeRequestsCount === 'function') {
          setHomeRequestsCount(count);
        }
      }
    }
  }
}

export function* countRequestsRequestSaga(action) {
  const profile = yield select(state => state.app.profile);
  const kapp = yield select(selectServicesKapp);
  const types = Array.isArray(action.payload)
    ? action.payload
    : !!action.payload
      ? [action.payload]
      : typeStates.keySeq().toJS();
  const counts = yield all(
    types
      .reduce(
        (map, type) =>
          map.set(
            type,
            call(searchSubmissions, {
              kapp: (kapp && kapp.slug) || 'services',
              limit: 0,
              search: buildSearch(
                {
                  coreState: typeStates.get(type),
                  all: type.includes('Other'),
                },
                profile,
              ),
              count: true,
            }),
          ),
        Map(),
      )
      .toJS(),
  );

  yield put(
    actions.countRequestsComplete(
      Map(counts)
        .map(
          countResults =>
            typeof countResults.count === 'number' ? countResults.count : null,
        )
        .toJS(),
    ),
  );
}

export function* watchRequests() {
  yield takeEvery(
    [
      types.FETCH_REQUESTS_REQUEST,
      types.FETCH_REQUESTS_CURRENT,
      types.FETCH_REQUESTS_PREVIOUS,
      types.FETCH_REQUESTS_NEXT,
    ],
    fetchRequestsRequestSaga,
  );
  yield takeEvery(types.COUNT_REQUESTS_REQUEST, countRequestsRequestSaga);
}
